<template>
  <div class="card card-custom card-stretch gutter-b">
    <div class="card-body d-flex flex-column">
      <div class="flex-grow-1 pb-5">
        <h1 style="text-transform: uppercase">{{ pageTitle }}</h1>
        <div class="d-flex align-items-center mb-6">
          <span class="text-muted font-weight-bold font-size-lg flex-grow-1">
            Verwalten Sie Empfänger für den automatischen monatlichen
            Statistikversand. Der Versand erfolgt immer als Excel-Datei für das
            aktuelle Jahr.
          </span>
        </div>

        <div class="vld-parent">
          <loading
            :active.sync="overlayActive"
            :can-cancel="false"
            :is-full-page="true"
          ></loading>
        </div>

        <v-app>
          <v-container>
            <v-row style="margin-top: 25px;">
              <v-col cols="12" class="d-flex justify-end text-center">
                <v-btn
                  class="primary custom_button"
                  large
                  height="50"
                  @click="openCreateDialog"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Empfänger anlegen
                </v-btn>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-alert
                  dense
                  outlined
                  type="info"
                  color="primary"
                  class="statistics-note"
                >
                  Monatlicher Auto-Versand, Excel-Format, aktuelles Jahr.
                  Versandtag je Empfänger wahlweise am 1. oder 15. des Monats.
                </v-alert>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-card-title class="px-0 pt-0">
                  <div class="statistics-recipients__toolbar">
                    <div class="statistics-recipients__summary">
                      {{ recipientsSummary }}
                    </div>
                  </div>
                </v-card-title>

                <v-alert v-if="error" dense outlined type="error" class="mb-4">
                  {{ error }}
                </v-alert>

                <v-data-table
                  :headers="headers"
                  :items="recipients"
                  class="table1"
                  :loading="loading.recipients"
                  loading-text="Bitte warten... Empfänger werden geladen"
                  :items-per-page="10"
                >
                  <template v-slot:footer.page-text="items">
                    {{ items.pageStart }} - {{ items.pageStop }} von
                    {{ items.itemsLength }}
                  </template>
                  <template v-slot:item.name="{ item }">
                    <div class="font-weight-bold">{{ item.name }}</div>
                  </template>
                  <template v-slot:item.send_day="{ item }">
                    <v-chip small outlined color="primary">
                      {{ formatSendDay(item.send_day) }}
                    </v-chip>
                  </template>
                  <template v-slot:item.clients_count="{ item }">
                    <div class="statistics-recipients__clients-count">
                      <span class="font-weight-bold">{{
                        item.clients.length
                      }}</span>
                      <span class="text-muted ml-1">Kunden</span>
                    </div>
                  </template>
                  <template v-slot:item.is_active="{ item }">
                    <div class="d-flex align-center justify-center">
                      <v-switch
                        v-model="item.is_active"
                        inset
                        dense
                        hide-details
                        class="mt-0 pt-0"
                        :disabled="isRecipientBusy(item.id)"
                        @change="toggleRecipientActive(item, $event)"
                      ></v-switch>
                      <span
                        :class="
                          item.is_active
                            ? 'text-success font-weight-bold'
                            : 'text-muted font-weight-bold'
                        "
                      >
                        {{ item.is_active ? "Aktiv" : "Inaktiv" }}
                      </span>
                    </div>
                  </template>
                  <template v-slot:item.last_sent_at="{ item }">
                    <span class="text-muted">{{
                      formatLastSentAt(item.last_sent_at)
                    }}</span>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <div class="statistics-recipients__actions">
                      <v-tooltip top attach="" dense color="primary">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            small
                            class="mr-2"
                            style="font-size: 20px;"
                            color="primary"
                            :disabled="isRecipientBusy(item.id)"
                            v-bind="attrs"
                            v-on="on"
                            @click="confirmSend(item)"
                          >
                            mdi-email
                          </v-icon>
                        </template>
                        <span>{{
                          isRecipientSending(item.id)
                            ? "Versand wird ausgelöst"
                            : "Jetzt senden"
                        }}</span>
                      </v-tooltip>
                      <v-tooltip top attach="" dense color="primary">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            small
                            class="mr-2"
                            style="font-size: 20px;"
                            :disabled="isRecipientBusy(item.id)"
                            v-bind="attrs"
                            v-on="on"
                            @click="openEditDialog(item)"
                          >
                            mdi-pencil
                          </v-icon>
                        </template>
                        <span>Bearbeiten</span>
                      </v-tooltip>
                      <v-tooltip top attach="" dense color="primary">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            small
                            style="font-size: 20px;"
                            :disabled="isRecipientBusy(item.id)"
                            v-bind="attrs"
                            v-on="on"
                            @click="confirmDelete(item)"
                          >
                            mdi-delete
                          </v-icon>
                        </template>
                        <span>Löschen</span>
                      </v-tooltip>
                    </div>
                  </template>
                  <template v-slot:no-data>
                    <div class="statistics-recipients__empty-state">
                      <div class="statistics-recipients__empty-title">
                        Keine Empfänger vorhanden
                      </div>
                      <div class="statistics-recipients__empty-text">
                        Legen Sie den ersten Empfänger für den monatlichen
                        Statistikversand an.
                      </div>
                      <v-btn
                        color="primary"
                        class="mt-4"
                        @click="openCreateDialog"
                      >
                        Empfänger anlegen
                      </v-btn>
                    </div>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>

            <v-dialog v-model="dialog" max-width="980px" persistent>
              <v-card>
                <v-card-title class="m-2 mb-4">
                  <span class="headline">{{ formTitle }}</span>
                </v-card-title>

                <v-card-text>
                  <v-alert dense outlined type="info" color="primary">
                    Der Versand erfolgt automatisch monatlich als XLSX-Datei für
                    das aktuelle Jahr {{ currentYear }}.
                  </v-alert>

                  <v-form ref="recipientForm" lazy-validation>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.name"
                          label="Name*"
                          filled
                          dense
                          clearable
                          background-color="#f7f7f7"
                          :rules="[rules.required]"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="form.email"
                          label="E-Mail-Adresse*"
                          filled
                          dense
                          clearable
                          background-color="#f7f7f7"
                          :rules="[rules.required, rules.email]"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select
                          v-model="form.send_day"
                          :items="sendDayOptions"
                          item-text="text"
                          item-value="value"
                          label="Versandtag*"
                          filled
                          dense
                          clearable
                          background-color="#f7f7f7"
                          :rules="[rules.required, rules.sendDay]"
                        ></v-select>
                      </v-col>
                      <v-col cols="12" md="4" class="d-flex align-center">
                        <v-switch
                          v-model="form.is_active"
                          inset
                          label="Empfänger aktiv"
                        ></v-switch>
                      </v-col>
                      <v-col cols="12">
                        <v-autocomplete
                          v-model="form.client_ids"
                          :items="clients"
                          item-text="CompanyName"
                          item-value="id"
                          label="Zugeordnete Kunden*"
                          filled
                          dense
                          multiple
                          small-chips
                          deletable-chips
                          clearable
                          background-color="#f7f7f7"
                          :rules="[rules.clients]"
                          :menu-props="{ closeOnContentClick: false }"
                        ></v-autocomplete>
                      </v-col>
                    </v-row>

                    <div v-if="error" class="fillAllFieldsMessage mt-4">
                      {{ error }}
                    </div>
                  </v-form>
                </v-card-text>

                <v-card-actions class="px-6 pb-6">
                  <v-spacer></v-spacer>
                  <v-btn class="mr-4 p-7" color="btn2" @click="closeDialog">
                    Schließen
                  </v-btn>
                  <v-btn
                    class="p-7"
                    color="primary"
                    :loading="saving"
                    :disabled="saving"
                    @click="submitForm"
                  >
                    Speichern
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-container>
        </v-app>
      </div>
    </div>
  </div>
</template>

<style>
.v-data-footer__select {
  display: none !important;
}

.custom_button.theme--light.v-btn.v-btn--disabled {
  background-color: lightgray !important;
}

.statistics-note {
  border-radius: 14px;
}

.statistics-recipients__toolbar {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.statistics-recipients__summary {
  color: #4a5568;
  font-size: 14px;
  font-weight: 600;
}

.statistics-recipients__clients-count {
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.statistics-recipients__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.statistics-recipients__empty-state {
  padding: 32px 16px;
  text-align: center;
}

.statistics-recipients__empty-title {
  color: #1f2937;
  font-size: 16px;
  font-weight: 700;
}

.statistics-recipients__empty-text {
  color: #6b7280;
  margin-top: 8px;
}

.fillAllFieldsMessage {
  color: red;
}
</style>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ApiService from "@/core/services/api.service";
import StatisticsRecipientsService from "@/core/services/statistics-recipients.service";
import { LOGOUT } from "@/core/services/store/auth.module";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import Swal from "sweetalert2";

const defaultForm = () => ({
  name: "",
  email: "",
  send_day: 1,
  is_active: true,
  client_ids: []
});

const normalizeRecipient = recipient => ({
  ...recipient,
  send_day: Number(recipient.send_day || 1),
  is_active: recipient.is_active === true || recipient.is_active === 1,
  clients: Array.isArray(recipient.clients) ? recipient.clients : []
});

export default {
  name: "statistics-recipients",
  components: { Loading },
  data: function() {
    return {
      pageTitle: "Statistikversand",
      dialog: false,
      recipients: [],
      selectedRecipient: null,
      form: defaultForm(),
      clients: [],
      loading: {
        page: false,
        recipients: false,
        clients: false,
        recipient: false
      },
      saving: false,
      error: null,
      busyRecipientIds: [],
      sendingRecipientIds: [],
      headers: [
        { text: "Name", value: "name" },
        { text: "E-Mail", value: "email" },
        { text: "Versandtag", value: "send_day", align: "center" },
        {
          text: "Kunden",
          value: "clients_count",
          align: "center",
          sortable: false
        },
        {
          text: "Aktiv",
          value: "is_active",
          align: "center",
          sortable: false
        },
        {
          text: "Letzter Versand",
          value: "last_sent_at",
          sortable: false
        },
        {
          text: "Aktionen",
          value: "actions",
          sortable: false,
          align: "center",
          width: "260px"
        }
      ],
      rules: {
        required: value => !!value || "Pflichtfeld.",
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return value ? pattern.test(value) || "Ungültige E-Mail." : true;
        },
        sendDay: value => {
          const day = Number(value);
          return (
            (Number.isInteger(day) && [1, 15].includes(day)) ||
            "Bitte wählen Sie den 1. oder 15. des Monats."
          );
        },
        clients: value => {
          return (
            (Array.isArray(value) && value.length > 0) ||
            "Bitte mindestens einen Kunden auswählen."
          );
        }
      }
    };
  },
  computed: {
    overlayActive: {
      get() {
        return this.loading.page || this.loading.recipient;
      },
      set() {}
    },
    currentYear() {
      return new Date().getFullYear();
    },
    formTitle() {
      return this.selectedRecipient
        ? "Statistik-Empfänger bearbeiten"
        : "Statistik-Empfänger anlegen";
    },
    recipientsSummary() {
      if (this.loading.recipients) {
        return "Empfänger werden geladen...";
      }

      if (!this.recipients.length) {
        return "Noch keine Empfänger angelegt";
      }

      return `${this.recipients.length} Empfänger konfiguriert`;
    },
    sendDayOptions() {
      return [
        { text: "1. des Monats", value: 1 },
        { text: "15. des Monats", value: 15 }
      ];
    }
  },
  created() {
    this.initializePage();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Statistikversand" }]);
  },
  methods: {
    async initializePage() {
      this.error = null;
      this.loading.page = true;

      try {
        await Promise.all([this.loadRecipients(), this.loadClients()]);
      } catch (error) {
        this.handleApiError(
          error,
          "Die Daten für den Statistikversand konnten nicht geladen werden."
        );
      } finally {
        this.loading.page = false;
      }
    },
    async loadRecipients() {
      this.loading.recipients = true;

      try {
        const { data } = await StatisticsRecipientsService.list();
        const recipients = Array.isArray(data.recipients)
          ? data.recipients
          : [];
        this.recipients = recipients.map(normalizeRecipient);
      } finally {
        this.loading.recipients = false;
      }
    },
    async loadClients() {
      this.loading.clients = true;

      try {
        const { data } = await ApiService.query("/clients/all");
        const clients = Array.isArray(data.clients) ? data.clients : [];
        this.clients = clients
          .slice()
          .sort((left, right) =>
            String(left.CompanyName || "").localeCompare(
              String(right.CompanyName || ""),
              "de"
            )
          );
      } finally {
        this.loading.clients = false;
      }
    },
    resetForm() {
      this.form = defaultForm();
      this.selectedRecipient = null;
      this.error = null;
      if (this.$refs.recipientForm) {
        this.$refs.recipientForm.resetValidation();
      }
    },
    openCreateDialog() {
      this.resetForm();
      this.dialog = true;
    },
    async openEditDialog(recipient) {
      this.error = null;
      this.loading.recipient = true;

      try {
        const { data } = await StatisticsRecipientsService.get(recipient.id);
        const resolvedRecipient = normalizeRecipient(
          data.recipient || recipient
        );
        this.selectedRecipient = resolvedRecipient;
        this.form = {
          name: resolvedRecipient.name || "",
          email: resolvedRecipient.email || "",
          send_day: Number(resolvedRecipient.send_day || 1),
          is_active: resolvedRecipient.is_active,
          client_ids: resolvedRecipient.clients.map(client => client.id)
        };
        this.dialog = true;
        this.$nextTick(() => {
          if (this.$refs.recipientForm) {
            this.$refs.recipientForm.resetValidation();
          }
        });
      } catch (error) {
        this.handleApiError(
          error,
          "Der Empfänger konnte nicht geladen werden."
        );
      } finally {
        this.loading.recipient = false;
      }
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();
    },
    buildPayload(source) {
      return {
        name: source.name.trim(),
        email: source.email.trim(),
        send_day: Number(source.send_day),
        is_active: !!source.is_active,
        client_ids: source.client_ids.slice()
      };
    },
    async submitForm() {
      this.error = null;

      if (!this.$refs.recipientForm.validate()) {
        return;
      }

      this.saving = true;

      try {
        const payload = this.buildPayload(this.form);

        if (this.selectedRecipient && this.selectedRecipient.id) {
          await StatisticsRecipientsService.update(
            this.selectedRecipient.id,
            payload
          );
        } else {
          await StatisticsRecipientsService.create(payload);
        }

        await this.loadRecipients();
        this.closeDialog();
      } catch (error) {
        this.handleApiError(
          error,
          "Der Empfänger konnte nicht gespeichert werden."
        );
      } finally {
        this.saving = false;
      }
    },
    async toggleRecipientActive(recipient, nextValue) {
      const previousValue = !nextValue;
      this.setRecipientBusy(recipient.id, true);
      this.error = null;

      try {
        await StatisticsRecipientsService.update(
          recipient.id,
          this.buildPayload({
            ...recipient,
            is_active: nextValue,
            client_ids: recipient.clients.map(client => client.id)
          })
        );
        await this.loadRecipients();
      } catch (error) {
        recipient.is_active = previousValue;
        this.handleApiError(
          error,
          "Der Status des Empfängers konnte nicht aktualisiert werden."
        );
      } finally {
        this.setRecipientBusy(recipient.id, false);
      }
    },
    async confirmDelete(recipient) {
      const result = await Swal.fire({
        title: "Empfänger löschen",
        text: "Möchten Sie diesen Statistik-Empfänger wirklich löschen?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc3545",
        confirmButtonText: "Löschen",
        cancelButtonText: "Abbrechen"
      });

      if (!result.value) {
        return;
      }

      this.setRecipientBusy(recipient.id, true);
      this.error = null;

      try {
        await StatisticsRecipientsService.delete(recipient.id);
        await this.loadRecipients();
      } catch (error) {
        this.handleApiError(
          error,
          "Der Empfänger konnte nicht gelöscht werden."
        );
      } finally {
        this.setRecipientBusy(recipient.id, false);
      }
    },
    async confirmSend(recipient) {
      const result = await Swal.fire({
        title: "Statistik jetzt senden?",
        text:
          "Der Versand wird sofort an diesen Empfänger für alle zugeordneten Kunden ausgelöst.",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#53a318",
        confirmButtonText: "Jetzt senden",
        cancelButtonText: "Abbrechen"
      });

      if (!result.value) {
        return;
      }

      this.error = null;
      this.setRecipientBusy(recipient.id, true);
      this.setRecipientSending(recipient.id, true);

      try {
        const { data } = await StatisticsRecipientsService.send(recipient.id);
        const attachmentsCount = Number(data.attachments_count || 0);

        if (attachmentsCount < 1) {
          Swal.fire({
            icon: "warning",
            title: "Versand nicht ausgelöst",
            text:
              data.message || "Keine Statistikdateien für den Versand erzeugt."
          });
          return;
        }

        Swal.fire({
          icon: "success",
          title: "Versand erfolgreich ausgelöst",
          showConfirmButton: false,
          timer: 2200,
          timerProgressBar: true
        });
      } catch (error) {
        const response = error && error.response ? error.response : null;
        Swal.fire({
          icon: "error",
          title: "Versand fehlgeschlagen",
          text:
            (response && response.data && response.data.message) ||
            "Die Statistik konnte nicht versendet werden."
        });
      } finally {
        this.setRecipientSending(recipient.id, false);
        this.setRecipientBusy(recipient.id, false);
      }
    },
    isRecipientBusy(id) {
      return (
        this.busyRecipientIds.includes(id) ||
        this.sendingRecipientIds.includes(id)
      );
    },
    setRecipientBusy(id, busy) {
      if (busy) {
        if (!this.busyRecipientIds.includes(id)) {
          this.busyRecipientIds = this.busyRecipientIds.concat(id);
        }
        return;
      }

      this.busyRecipientIds = this.busyRecipientIds.filter(
        recipientId => recipientId !== id
      );
    },
    isRecipientSending(id) {
      return this.sendingRecipientIds.includes(id);
    },
    setRecipientSending(id, sending) {
      if (sending) {
        if (!this.sendingRecipientIds.includes(id)) {
          this.sendingRecipientIds = this.sendingRecipientIds.concat(id);
        }
        return;
      }

      this.sendingRecipientIds = this.sendingRecipientIds.filter(
        recipientId => recipientId !== id
      );
    },
    formatLastSentAt(value) {
      if (!value) {
        return "Noch kein Versand";
      }

      return new Date(value).toLocaleString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    formatSendDay(value) {
      const day = Number(value);
      if (day === 15) {
        return "15. des Monats";
      }

      return "1. des Monats";
    },
    handleApiError(error, fallbackMessage) {
      const response = error && error.response ? error.response : null;

      if (response && response.status === 401) {
        this.$store
          .dispatch(LOGOUT)
          .then(() => this.$router.push({ name: "login" }));
        return;
      }

      this.error =
        (response && response.data && response.data.message) || fallbackMessage;
    }
  }
};
</script>
